import React, { useEffect, useRef, useState } from 'react';
import { UsersIcon } from '@heroicons/react/24/outline';
import FilterChip from './FilterChip';

// Multi-select forecaster picker (chip + popover). Shared by the Forecasts page
// and the main dashboard chart toolbar. `forecasters` = [{ id, name }]; callers
// decide what `name` to show (e.g. email vs. username alias by role/privacy).
// `selectedIds` is an array of ids, or null meaning "all". onChange emits the
// new array, or null when everything is selected (keeps the "all" default).
export const ForecasterPicker = ({
  forecasters = [],
  selectedIds,
  onChange,
  open: openProp,
  onOpenChange,
}) => {
  // Controlled when the parent supplies onOpenChange (so it can coordinate
  // one-popover-open-at-a-time with the other filter chips); otherwise manages
  // its own open state.
  const isControlled = onOpenChange != null;
  const [internalOpen, setInternalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const open = isControlled ? !!openProp : internalOpen;
  const setOpen = (value) => (isControlled ? onOpenChange(value) : setInternalOpen(value));
  const ref = useRef(null);

  useEffect(() => {
    if (!open || isControlled) return undefined; // parent handles outside-click when controlled
    const onOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isControlled]);

  const allIds = forecasters.map((f) => f.id);
  const selectedSet = new Set(selectedIds == null ? allIds : selectedIds);

  const toggle = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next.size === forecasters.length ? null : [...next]);
  };

  const filtered = forecasters.filter((f) =>
    f.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} className="relative">
      <FilterChip
        icon={UsersIcon}
        label="Forecasters"
        badge={selectedSet.size}
        active={open}
        onClick={() => setOpen(!open)}
      />
      {open && (
        <div className="absolute left-0 z-50 mt-2 w-64 rounded-lg bg-white dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-lg p-2">
          <input
            type="text"
            aria-label="Search forecasters"
            placeholder="Search forecasters…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full mb-2 rounded-md border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 px-2 py-1.5 text-xs text-gray-900 dark:text-gray-100"
          />
          <div className="flex gap-2 mb-2">
            <button
              type="button"
              onClick={() => onChange(null)}
              className="flex-1 px-2 py-1 text-[11px] font-semibold rounded bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50"
            >
              All
            </button>
            <button
              type="button"
              onClick={() => onChange([])}
              className="flex-1 px-2 py-1 text-[11px] font-semibold rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              None
            </button>
          </div>
          <div className="max-h-56 overflow-y-auto space-y-0.5">
            {filtered.length === 0 ? (
              <div className="text-xs text-gray-500 py-3 text-center">No forecasters</div>
            ) : (
              filtered.map((f) => (
                <label
                  key={f.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={selectedSet.has(f.id)}
                    onChange={() => toggle(f.id)}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="truncate text-gray-700 dark:text-gray-200">{f.name}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ForecasterPicker;
